import { DEFAULT_VIDEO_CONSTRAINTS, SELECTED_AUDIO_INPUT_KEY, SELECTED_VIDEO_INPUT_KEY } from '../../../constants';
import { getDeviceInfo, isPermissionDenied } from '../../../utils';
import { useCallback, useState } from 'react';
import Video, { LocalVideoTrack, LocalAudioTrack, CreateLocalTrackOptions } from 'twilio-video';
import { useAppState } from '../../../state';

// Path where Krisp WASM files are hosted (relative to public folder)
const KRISP_ASSETS_PATH = '/krisp';

/**
 * Keywords that typically indicate a headset or external audio device.
 * These are checked against device labels (case-insensitive).
 */
const HEADSET_KEYWORDS = [
  'headset',
  'headphone',
  'earphone',
  'earbuds',
  'airpods',
  'jabra',
  'plantronics',
  'poly',
  'logitech',
  'sennheiser',
  'bose',
  'sony wh',
  'sony wf',
  'beats',
  'usb audio',
  'usb microphone',
  'external',
];

/**
 * Helper function to check if a device is likely a headset based on its label.
 */
function isHeadsetDevice(device: MediaDeviceInfo): boolean {
  const label = device.label.toLowerCase();
  return HEADSET_KEYWORDS.some(keyword => label.includes(keyword));
}

/**
 * Helper function to find the preferred device ID from a list of devices.
 * Priority order:
 * 1. Headset/external audio device (if connected)
 * 2. System default device (deviceId === 'default')
 * 3. First device in the list as fallback
 */
function getDefaultDeviceId(devices: MediaDeviceInfo[]): string | undefined {
  // First, try to find a headset device
  const headsetDevice = devices.find(device => isHeadsetDevice(device));
  if (headsetDevice) {
    console.log('[DeviceSelection] Headset detected:', headsetDevice.label);
    return headsetDevice.deviceId;
  }

  // Second, try to find the device with 'default' deviceId (system default)
  const defaultDevice = devices.find(device => device.deviceId === 'default' || device.label.toLowerCase().includes('default'));
  if (defaultDevice) {
    return defaultDevice.deviceId;
  }
  
  // Fallback: return the first device in the list (browsers typically list default first)
  return devices.length > 0 ? devices[0].deviceId : undefined;
}

export default function useLocalTracks() {
  const [audioTrack, setAudioTrack] = useState<LocalAudioTrack>();
  const [videoTrack, setVideoTrack] = useState<LocalVideoTrack>();
  const [isAcquiringLocalTracks, setIsAcquiringLocalTracks] = useState(false);
  const { meeting, settings } = useAppState();

  const getLocalAudioTrack = useCallback((deviceId?: string) => {
    const noiseCancellationEnabled = settings?.noiseCancellationEnabled ?? true;
    
    const options: CreateLocalTrackOptions = {
      ...(deviceId && { deviceId: { exact: deviceId } }),
      // Krisp noise cancellation options
      ...(noiseCancellationEnabled && {
        noiseCancellationOptions: {
          sdkAssetsPath: KRISP_ASSETS_PATH,
          vendor: 'krisp'
        },
        // Disable browser's built-in noise suppression when using Krisp
        noiseSuppression: true
      }),
    };

    return Video.createLocalAudioTrack(options).then(newTrack => {
      // Log noise cancellation status
      if (newTrack.noiseCancellation) {
        console.log('[Krisp] Noise cancellation enabled successfully');
      } else if (noiseCancellationEnabled) {
        console.warn('[Krisp] Noise cancellation not available, using browser fallback');
      }
      setAudioTrack(newTrack);
      return newTrack;
    });
  }, [settings?.noiseCancellationEnabled]);

  const getLocalVideoTrack = useCallback(async () => {
    const selectedVideoDeviceId = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);

    const { videoInputDevices } = await getDeviceInfo();

    const hasSelectedVideoDevice = videoInputDevices.some(
      device => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId
    );

    // If no device was previously selected, prefer the system default device
    const deviceIdToUse = hasSelectedVideoDevice 
      ? selectedVideoDeviceId! 
      : getDefaultDeviceId(videoInputDevices);

    const options: CreateLocalTrackOptions = {
      ...(DEFAULT_VIDEO_CONSTRAINTS as {}),
      name: `camera-${Date.now()}`,
      ...(deviceIdToUse && { deviceId: { exact: deviceIdToUse } }),
    };

    return Video.createLocalVideoTrack(options).then(newTrack => {
      setVideoTrack(newTrack);
      return newTrack;
    });
  }, []);

  const removeLocalAudioTrack = useCallback(() => {
    if (audioTrack) {
      audioTrack.stop();
      setAudioTrack(undefined);
    }
  }, [audioTrack]);

  const removeLocalVideoTrack = useCallback(() => {
    if (videoTrack) {
      videoTrack.stop();
      setVideoTrack(undefined);
    }
  }, [videoTrack]);

  const getAudioAndVideoTracks = useCallback(async () => {
    const { audioInputDevices, videoInputDevices, hasAudioInputDevices, hasVideoInputDevices } = await getDeviceInfo();

    // if (meeting && Boolean(meeting.isChaperoning)) return Promise.resolve();
    if (!hasAudioInputDevices && !hasVideoInputDevices) return Promise.resolve();
    if (isAcquiringLocalTracks || audioTrack || videoTrack) return Promise.resolve();

    setIsAcquiringLocalTracks(true);

    // For Audio
    const selectedAudioDeviceId = window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY);

    const hasSelectedAudioDevice = audioInputDevices.some(
      device => selectedAudioDeviceId && device.deviceId === selectedAudioDeviceId
    );

    // In Chrome, it is possible to deny permissions to only audio or only video.
    // If that has happened, then we don't want to attempt to acquire the device.
    const isMicrophonePermissionDenied = await isPermissionDenied('microphone');

    const shouldAcquireAudio = hasAudioInputDevices && !isMicrophonePermissionDenied;

    // For Video
    let shouldAcquireVideo = false;
    let isCameraPermissionDenied = true;
    const selectedVideoDeviceId = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);

    const hasSelectedVideoDevice = videoInputDevices.some(
      device => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId
    );

    // if (meeting && Boolean(meeting.isChaperoning) === false) {
    //   // In Chrome, it is possible to deny permissions to only audio or only video.
    //   // If that has happened, then we don't want to attempt to acquire the device.
    //   isCameraPermissionDenied = await isPermissionDenied('camera');

    //   shouldAcquireVideo = hasVideoInputDevices && !isCameraPermissionDenied;
    // }

    //set video-permission default to false
    // shouldAcquireVideo = false;

    const noiseCancellationEnabled = settings?.noiseCancellationEnabled ?? true;

    // If no device was previously selected, prefer the system default device
    const audioDeviceIdToUse = hasSelectedAudioDevice 
      ? selectedAudioDeviceId! 
      : getDefaultDeviceId(audioInputDevices);
    const videoDeviceIdToUse = hasSelectedVideoDevice 
      ? selectedVideoDeviceId! 
      : getDefaultDeviceId(videoInputDevices);

    // Build audio constraints with optional noise cancellation
    const audioConstraints = shouldAcquireAudio ? {
      ...(audioDeviceIdToUse ? { deviceId: { exact: audioDeviceIdToUse } } : {}),
      // Krisp noise cancellation options
      ...(noiseCancellationEnabled && {
        noiseCancellationOptions: {
          sdkAssetsPath: KRISP_ASSETS_PATH,
          vendor: 'krisp'
        },
        // Disable browser's built-in noise suppression when using Krisp
        noiseSuppression: true
      }),
    } : false;

    const localTrackConstraints = {
      video: shouldAcquireVideo && {
        ...(DEFAULT_VIDEO_CONSTRAINTS as {}),
        name: `camera-${Date.now()}`,
        ...(videoDeviceIdToUse && { deviceId: { exact: videoDeviceIdToUse } }),
      },
      audio: audioConstraints,
    };

    // console.log("tv:: localTrackConstraints", localTrackConstraints);

    return Video.createLocalTracks(localTrackConstraints)
      .then(tracks => {
        // console.log("tv:: tracks", tracks);
        const newVideoTrack = tracks.find(track => track.kind === 'video') as LocalVideoTrack;
        const newAudioTrack = tracks.find(track => track.kind === 'audio') as LocalAudioTrack;
        if (newVideoTrack) {
          setVideoTrack(newVideoTrack);
          // Save the deviceId so it can be picked up by the VideoInputList component. This only matters
          // in cases where the user's video is disabled.
          window.localStorage.setItem(
            SELECTED_VIDEO_INPUT_KEY,
            newVideoTrack.mediaStreamTrack.getSettings().deviceId ?? ''
          );
        }
        if (newAudioTrack) {
          setAudioTrack(newAudioTrack);
        }

        // These custom errors will be picked up by the MediaErrorSnackbar component.
        // if (isCameraPermissionDenied && isMicrophonePermissionDenied) {
        //   const error = new Error();
        //   error.name = 'NotAllowedError';
        //   throw error;
        // }

        // if (isCameraPermissionDenied) {
        //   throw new Error('CameraPermissionsDenied');
        // }

        if (isMicrophonePermissionDenied) {
          throw new Error('MicrophonePermissionsDenied');
        }
      })
      .finally(() => setIsAcquiringLocalTracks(false));
  }, [audioTrack, videoTrack, isAcquiringLocalTracks, meeting]);

  const localTracks = [audioTrack, videoTrack].filter(track => track !== undefined) as (
    | LocalAudioTrack
    | LocalVideoTrack
  )[];

  const audioTracks = localTracks.find(track => track.kind === 'audio') as LocalAudioTrack;
  if (audioTracks && meeting && Boolean(meeting.isChaperoning) === true) {
    audioTracks.disable();
  }

  return {
    localTracks,
    getLocalVideoTrack,
    getLocalAudioTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
  };
}
